<script>
import { GAME_OVERRIDE } from "vue-chess-shared/constants";
import { firestore } from "@/services/firebase";
import { uuidv4 } from "vue-chess-shared/utils";
import TokenHelper from "@/utils/token-helper";
import MatchHelper from "@/utils/match-helper";
import { randomBotPlayer } from "vue-chess-shared/utils/ai-helper";
import { MATCH_DEFAULT } from "../../constants";
import { mapGetters, mapMutations } from "vuex";
import firebase from "firebase/app";
import { initFirestoreTimeOffset } from "@/services/firebase";
import { serverTimestamp } from "@/utils/firebase-helper";

const matchesRef = firestore.collection("matches");
const openMatchesRef = firestore.collection("openMatches");

export default {
  name: "lobbyRenderless",

  data() {
    return {
      isLoading: false,
      createdMatches: [],
      liveMatches: [],
      // recentMatches: [],
      waitingTime: null,
      timerInterval: null,
      firestoreTimeOffset: 0,
      rtdbTimeOffset: 0,
      filterName: null,
    };
  },

  computed: {
    ...mapGetters(["getUserId", "getPlayer"]),

    userId() {
      return this.getUserId;
    },

    matches() {
      return [...this.createdMatches, ...this.liveMatches];
    },

    // if there is an open or live match, then watch the match and automatically back to it in a watch
    // except for the simul match because users are allowed to play other games
    // as long as they are not hosting a any simul
    currentMatch() {
      return this.matches.find(
        (m) =>
          !m.result &&
          ((m.player1 && m.player1.user_id === this.userId) ||
            (m.player2 && m.player2.user_id === this.userId))
      );
    },
  },

  firestore: {
    createdMatches: openMatchesRef.where("status", "==", "created"),
    liveMatches: matchesRef.where("status", "==", "live"),
  },

  watch: {
    currentMatch: {
      immediate: true, // call it upon creation too
      handler(currentMatch) {
        this.setCurrentMatch(currentMatch);

        if (!currentMatch) {
          this.stopTimer();
          return;
        }

        if (currentMatch.status === "live") {
          this.stopTimer();
          return;
        }

        this.startTimer();
      },
    },

    createdMatches: function() {
      if (!this.createdMatches || !this.createdMatches.length) return;
      this.createdMatches.forEach((match) => {
        if (!match.createdAt) return;
        const elapsed =
          new Date() -
          match.createdAt.toDate() +
          this.firestoreTimeOffset * 1000;
        if (elapsed > 300000) this.cancelMatch(match);
      });
    },
  },

  methods: {
    ...mapMutations(["setCurrentMatch"]),

    async createAiMatch(
      duration = MATCH_DEFAULT.DURATION,
      increment = MATCH_DEFAULT.INCREMENT,
      variant = MATCH_DEFAULT.VARIANT
    ) {
      if (!this.getPlayer) return;
      if (this.currentMatch) return;
      if (await TokenHelper.checkExpiry()) return;

      this.isLoading = true;
      const matchId = uuidv4();
      await MatchHelper.createMatch({
        matchId: matchId,
        whitePlayer: {
          ...this.getPlayer,
          timeOffset: this.firestoreTimeOffset,
        },
        blackPlayer: {
          ...randomBotPlayer(),
          timeOffset: this.firestoreTimeOffset,
        },
        gameOverride: GAME_OVERRIDE.PVAI,
        duration,
        increment,
        variant,
      });
      this.isLoading = false;
    },

    createOrJoinMatch(
      duration = MATCH_DEFAULT.DURATION,
      increment = MATCH_DEFAULT.INCREMENT,
      variant = MATCH_DEFAULT.VARIANT
    ) {
      if (this.currentMatch) return;
      for (const match of this.createdMatches) {
        if (
          match.duration === duration &&
          match.increment === increment &&
          match.variant === variant
        ) {
          return this.joinMatch(match);
        }
      }
      return this.createMatch(duration, increment, variant);
    },

    async createMatch(
      duration = MATCH_DEFAULT.DURATION,
      increment = MATCH_DEFAULT.INCREMENT,
      variant = MATCH_DEFAULT.VARIANT
    ) {
      if (!this.getPlayer) return;
      if (this.currentMatch) return;
      if (await TokenHelper.checkExpiry()) return;

      this.isLoading = true;
      const matchId = uuidv4();
      await openMatchesRef.doc(matchId).set({
        match_id: matchId,
        duration,
        increment,
        variant,
        player1: { ...this.getPlayer, timeOffset: this.firestoreTimeOffset },
        status: "created",
        result: "",
        createdAt: serverTimestamp(),
      });
      this.isLoading = false;
    },

    async cancelMatch(match) {
      if (!match || match.status !== "created") return;

      this.isLoading = true;
      await openMatchesRef.doc(match.id).update({
        status: "canceled",
        updatedAt: serverTimestamp(),
      });
      this.isLoading = false;
    },

    expireMatch(match) {
      console.log("expireMatch", match.id, this.waitingTime);
      if (!match || match.status !== "created") return;
      this.stopTimer();
      openMatchesRef.doc(match.id).update({
        status: "expired",
        expiredAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    },

    async joinMatch(match) {
      if (!match) return;
      if (this.currentMatch) return;
      if (!this.getPlayer) return;
      if (match.player2) return;
      if (match.player1.user_id === this.getPlayer.user__id) return;
      if (match.status !== "created") return;
      if (await TokenHelper.checkExpiry()) return;

      this.isLoading = true;
      let whitePlayer = match.player1;
      let blackPlayer = {
        ...this.getPlayer,
        timeOffset: this.firestoreTimeOffset,
      };

      openMatchesRef.doc(match.id).update({
        player1: whitePlayer,
        player2: blackPlayer,
        status: "started",
        updatedAt: serverTimestamp(),
      });

      if (Math.random() < 0.5) {
        const temp = whitePlayer;
        whitePlayer = blackPlayer;
        blackPlayer = temp;
      }

      const matchId = await MatchHelper.createMatch({
        matchId: match.id,
        whitePlayer: whitePlayer,
        blackPlayer: blackPlayer,
        gameOverride: GAME_OVERRIDE.PVP,
        duration: match.duration,
        increment: match.increment,
        variant: match.variant,
      });
      if (!matchId) {
        this.$buefy.toast.open("The match is no longer available");
      }
      this.isLoading = false;
    },

    startTimer() {
      if (!this.currentMatch) return;
      if (!this.currentMatch.createdAt) return;
      if (this.currentMatch.startedAt) return;
      if (this.timerInterval) return;
      this.timerInterval = setInterval(() => {
        if (!this.currentMatch || !this.currentMatch.createdAt) {
          return this.stopTimer();
        }
        this.waitingTime =
          firebase.firestore.Timestamp.fromDate(new Date()) -
          this.currentMatch.createdAt +
          this.firestoreTimeOffset;

        if (this.waitingTime > 300) {
          this.expireMatch(this.currentMatch);
        }
      }, 1000);
    },

    stopTimer() {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
      this.waitingTime = null;
    },
  },

  created() {
    initFirestoreTimeOffset();
    this.firestoreTimeOffset = parseFloat(
      sessionStorage.getItem("firestoreTimeOffset")
    );
    this.rtdbTimeOffset = parseInt(sessionStorage.getItem("rtdbTimeOffset"));
    if (!this.rtdbTimeOffset) this.rtdbTimeOffset = 0;
  },

  beforeDestroy() {
    this.stopTimer();
    if (this.currentMatch) this.cancelMatch(this.currentMatch);
  },
};
</script>
